import React from "react";
import { IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import MoreIcon from "@mui/icons-material/MoreVert";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import ScienceIcon from "@mui/icons-material/Science";
import BarChartIcon from "@mui/icons-material/BarChart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DescriptionIcon from "@mui/icons-material/Description";

import { Link, useNavigate } from "react-router-dom";
import { DeleteDocument } from "api/DocumentAPI";

const TYPE_META = {
  research:    { label: "Research Paper",   color: "#1565C0", bg: "#e8f0fe", Icon: ScienceIcon },
  business:    { label: "Business Report",  color: "#2E7D32", bg: "#e8f5e9", Icon: BarChartIcon },
  literature:  { label: "Literature Review",color: "#6A1B9A", bg: "#f3e5f5", Icon: MenuBookIcon },
  "case-study":{ label: "Case Study",       color: "#E65100", bg: "#fff3e0", Icon: FindInPageIcon },
  technical:   { label: "Technical Report", color: "#37474F", bg: "#eceff1", Icon: EngineeringIcon },
  article:     { label: "Article",          color: "#757575", bg: "#f5f5f5", Icon: DescriptionIcon },
  story:       { label: "Story",            color: "#757575", bg: "#f5f5f5", Icon: DescriptionIcon },
};

function similarity(s1, s2) {
  if (!s1?.length || !s2?.length) return 0;
  const longer = s1.length >= s2.length ? s1 : s2;
  const shorter = s1.length < s2.length ? s1 : s2;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;

  const s1l = longer.toLowerCase();
  const s2l = shorter.toLowerCase();
  const costs = [];
  for (let i = 0; i <= s1l.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2l.length; j++) {
      if (i === 0) costs[j] = j;
      else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1l.charAt(i - 1) !== s2l.charAt(j - 1))
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[s2l.length] = lastValue;
  }
  return (longerLength - costs[s2l.length]) / parseFloat(longerLength);
}

function DocumentCard({ documents }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [errors, setErrors] = React.useState({});
  const [menuDocId, setMenuDocId] = React.useState(null);
  const [search, setSearch] = React.useState("");

  const { deleteDocument } = DeleteDocument(setErrors);
  const navigate = useNavigate();

  const handleMenu = (e, id) => {
    e.stopPropagation();
    setMenuDocId(id);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const filtered = React.useMemo(() => {
    if (!documents) return [];
    if (!search.trim()) return documents;
    return [...documents]
      .map((doc) => ({ doc, score: similarity(doc.title ?? "", search) }))
      .sort((a, b) => b.score - a.score)
      .map(({ doc }) => doc);
  }, [documents, search]);

  const isEmpty = !documents || documents.length === 0;

  return (
    <div style={{ maxWidth: 1100, margin: "24px auto", padding: "0 24px 48px" }}>
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Typography
          variant="caption"
          style={{
            color: "#888",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          Recent documents{documents?.length ? ` · ${documents.length}` : ""}
        </Typography>
        <TextField
          size="small"
          placeholder="Search documents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 18, color: "#aaa" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 220,
            "& .MuiOutlinedInput-root": { borderRadius: 3, backgroundColor: "white" },
          }}
        />
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            backgroundColor: "white",
            borderRadius: 12,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}
        >
          <DescriptionIcon style={{ fontSize: 48, color: "#ddd", marginBottom: 12 }} />
          <Typography style={{ color: "#aaa", fontFamily: "sans-serif" }}>
            No documents yet — start one above.
          </Typography>
        </div>
      )}

      {/* Document grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {filtered.map((doc) => {
          const meta = TYPE_META[doc.type] ?? TYPE_META.article;
          const Icon = meta.Icon;
          return (
            <div
              key={doc.id}
              onClick={() => navigate(`/page/${doc.id}`)}
              style={{
                backgroundColor: "white",
                borderRadius: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                cursor: "pointer",
                overflow: "hidden",
                transition: "box-shadow 0.15s ease, transform 0.15s ease",
                display: "flex",
                flexDirection: "column",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.07)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {/* Colour bar */}
              <div style={{ height: 4, backgroundColor: meta.color }} />

              <div style={{ padding: "16px 16px 12px", flex: 1 }}>
                {/* Type badge */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    backgroundColor: meta.bg,
                    color: meta.color,
                    borderRadius: 20,
                    padding: "2px 8px",
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    marginBottom: 10,
                  }}
                >
                  <Icon style={{ fontSize: 12 }} />
                  {meta.label}
                </div>

                {/* Title */}
                <Typography
                  style={{
                    fontWeight: 700,
                    fontSize: "1rem",
                    color: "#1a1a2e",
                    lineHeight: 1.3,
                    marginBottom: 8,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                  }}
                >
                  {doc.title || "Untitled Document"}
                </Typography>

                {/* Preview */}
                <Typography
                  style={{
                    fontSize: "0.78rem",
                    color: "#888",
                    lineHeight: 1.5,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }}
                >
                  {doc.content || "Empty document"}
                </Typography>
              </div>

              {/* Footer row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px 10px",
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <Typography style={{ fontSize: "0.7rem", color: "#bbb" }}>
                  {doc.lastEdited ? `Edited ${doc.lastEdited}` : ""}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenu(e, doc.id)}
                  style={{ color: "#ccc" }}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          );
        })}
      </div>

      {/* Context menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={(e) => {
            handleClose(e);
            navigate(`/page/${menuDocId}`);
          }}
        >
          <DriveFileRenameOutlineIcon style={{ marginRight: 8, fontSize: 18 }} />
          Open
        </MenuItem>
        <Link to={`/page/${menuDocId}`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
          <MenuItem onClick={handleClose}>
            <OpenInNewIcon style={{ marginRight: 8, fontSize: 18 }} />
            Open in new tab
          </MenuItem>
        </Link>
        <MenuItem
          onClick={(e) => {
            handleClose(e);
            deleteDocument({ variables: { id: menuDocId } });
          }}
          style={{ color: "#d32f2f" }}
        >
          <DeleteForeverIcon style={{ marginRight: 8, fontSize: 18 }} />
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
}

export default DocumentCard;
